// @ts-nocheck
import React from 'react';
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ArrowLeft, MessageSquare, Star, User } from "lucide-react";
import { useAuth } from "@/contexts/auth-context";

export function GivenFeedbackPage() {
  const { user } = useAuth();

  // Mock data for feedback given by the current user
  const givenFeedback = [
    {
      id: '1',
      title: 'Q3 Performance Review',
      content: 'Excellent technical skills and great collaboration with the team. Always willing to help others.',
      category: 'Performance',
      rating: 5,
      recipient: {
        name: 'Sarah Johnson',
        avatar: null,
        initials: 'SJ'
      },
      createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 5), // 5 days ago
      feedbackType: 'targeted',
      isAnonymous: false,
    },
    {
      id: '2',
      title: 'Team Collaboration Review',
      content: 'Good communication skills and always meets deadlines. Could improve on providing more detailed documentation.',
      category: 'Communication',
      rating: 4,
      recipient: {
        name: 'David Smith',
        avatar: null,
        initials: 'DS'
      },
      createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 12), // 12 days ago
      feedbackType: 'regular',
      isAnonymous: false,
    }
  ].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

  return (
    <div className="min-h-screen bg-gradient-to-br from-muted/30 via-background to-muted/30 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link
            to="/feedback/total"
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            Back to Total Feedback
          </Link>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 mb-8">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-50 to-violet-50 rounded-2xl flex items-center justify-center">
              <MessageSquare className="h-8 w-8 text-purple-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Feedback Given</h1>
              <p className="text-muted-foreground text-lg">
                All feedback you've provided to colleagues
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <Badge className="bg-purple-100 text-purple-800 border-purple-200 px-3 py-1 text-sm font-medium">
              {givenFeedback.length} Given
            </Badge>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl flex items-center justify-center">
                  <MessageSquare className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-foreground">{givenFeedback.length}</div>
                  <div className="text-sm text-muted-foreground">Total Given</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl flex items-center justify-center">
                  <Star className="h-6 w-6 text-yellow-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-foreground">
                    {givenFeedback.filter(f => f.rating).length > 0
                      ? (givenFeedback.reduce((sum, f) => sum + (f.rating || 0), 0) / givenFeedback.filter(f => f.rating).length).toFixed(1)
                      : '0'}
                  </div>
                  <div className="text-sm text-muted-foreground">Average Rating</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl flex items-center justify-center">
                  <Star className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-foreground">
                    {givenFeedback.filter(f => f.feedbackType === 'targeted').length}
                  </div>
                  <div className="text-sm text-muted-foreground">Targeted Feedback</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl flex items-center justify-center">
                  <User className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-foreground">
                    {new Set(givenFeedback.map(f => f.recipient.name)).size}
                  </div>
                  <div className="text-sm text-muted-foreground">Recipients</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Empty State */}
        {givenFeedback.length === 0 && (
          <Card>
            <CardContent className="text-center py-12">
              <div className="w-16 h-16 bg-gradient-to-br from-muted/30 to-muted rounded-2xl flex items-center justify-center mx-auto mb-4">
                <MessageSquare className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">No feedback given yet</h3>
              <p className="text-muted-foreground mb-6">
                Once you provide feedback to colleagues, it will appear here.
              </p>
              <Link to="/feedback">
                <Button>
                  Back to Feedback Dashboard
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Given Feedback List */}
        {givenFeedback.length > 0 && (
          <div className="space-y-6">
            {givenFeedback.map((item) => (
              <Card key={item.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                        item.feedbackType === 'targeted'
                          ? 'bg-gradient-to-br from-purple-50 to-pink-50'
                          : 'bg-gradient-to-br from-indigo-50 to-purple-50'
                      }`}>
                        {item.feedbackType === 'targeted' ? (
                          <Star className="h-6 w-6 text-purple-600" />
                        ) : (
                          <User className="h-6 w-6 text-indigo-600" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <div className="font-bold text-foreground text-lg">{item.title}</div>
                          <Badge
                            variant="outline"
                            className={`rounded-lg px-3 py-1 border ${
                              item.feedbackType === 'targeted'
                                ? 'bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 border-purple-200'
                                : 'bg-gradient-to-r from-indigo-50 to-purple-50 text-indigo-700 border-indigo-200'
                            }`}
                          >
                            {item.category}
                          </Badge>
                        </div>
                        <div className="text-muted-foreground">
                          For {item.recipient.name} • {item.createdAt.toLocaleDateString()}
                        </div>
                      </div>

                      {/* Recipient Avatar */}
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={item.recipient.avatar} />
                          <AvatarFallback className="bg-blue-100 text-blue-700">
                            {item.recipient.initials}
                          </AvatarFallback>
                        </Avatar>
                        <div className="text-right">
                          <div className="font-medium text-foreground">{item.recipient.name}</div>
                          <div className="text-sm text-muted-foreground">Recipient</div>
                        </div>
                      </div>
                    </div>
                    {item.rating && (
                      <div className="flex items-center gap-2 bg-yellow-50 px-3 py-1 rounded-lg border border-yellow-200 ml-4">
                        <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                        <span className="text-sm font-bold text-yellow-700">{item.rating}</span>
                      </div>
                    )}
                  </div>
                  <div className={`p-4 rounded-xl ${
                    item.feedbackType === 'targeted' ? 'bg-muted/50 border border-border' : 'bg-muted/30'
                  }`}>
                    <p className="text-muted-foreground leading-relaxed">
                      "{item.content}"
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default GivenFeedbackPage
